import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Icon from './Icon';
import { APP_ICON_IDS, iconStyleColorToSx, iconStyleSizeToGridFontSize } from './iconPicker';

interface IconGridProps {
  selectedIcon?: string;
  onSelect: (iconId: string) => void;
  /** Preview color for grid icons (TinyMCE style color key or MUI palette path). */
  previewColor?: string;
  previewSize?: string;
  maxHeight?: number;
  searchTerm?: string;
  onSearchTermChange?: (term: string) => void;
}

export default function IconGrid({
  selectedIcon = '',
  onSelect,
  previewColor = 'primary',
  previewSize = 'medium',
  maxHeight = 400,
  searchTerm: controlledSearch,
  onSearchTermChange,
}: IconGridProps) {
  const { t } = useTranslation();
  const [internalSearch, setInternalSearch] = useState('');
  const searchTerm = controlledSearch ?? internalSearch;
  const setSearchTerm = onSearchTermChange ?? setInternalSearch;

  const filteredIcons = useMemo(() => {
    if (!searchTerm.trim()) return APP_ICON_IDS;
    const q = searchTerm.toLowerCase();
    return APP_ICON_IDS.filter(id => id.toLowerCase().includes(q));
  }, [searchTerm]);

  const iconSx = {
    fontSize: iconStyleSizeToGridFontSize(previewSize),
    color: previewColor.includes('.') ? previewColor : iconStyleColorToSx(previewColor),
  };

  return (
    <Box>
      <TextField
        fullWidth
        label={t('editor.iconSelector.search')}
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
        slotProps={{
          input: {
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          },
        }}
      />
      <Box sx={{ maxHeight, overflow: 'auto' }}>
        <Grid container spacing={1}>
          {filteredIcons.map(iconName => (
            <Grid size={{ xs: 4, sm: 3, md: 2 }} key={iconName}>
              <Tooltip title={iconName} placement="top">
                <Box
                  sx={{
                    p: 1.25,
                    minHeight: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: selectedIcon === iconName ? 2 : 1,
                    borderColor: selectedIcon === iconName ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    textAlign: 'center',
                    backgroundColor: selectedIcon === iconName ? 'action.selected' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onClick={() => onSelect(iconName)}
                >
                  <Icon icon={iconName} sx={iconSx} />
                </Box>
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
